import type {
  ShipmentShipTrip,
  ShipmentTrackingSummary,
} from 'common/src/utils/types';
import { getShipmentCalendarDayOffset } from '../../../utils/shipment-logistics';
import type { Arrival, ArrivalMode } from '../arrival-manager/model';

export type ScheduleSort = 'ETD real' | 'ETA real' | 'Atraso';
export type ScheduleModeFilter = 'all' | ArrivalMode;

/** One sailing leg of a transshipped route, as days from today. */
export interface ScheduleLeg {
  vessel: string;
  start: number;
  end: number;
  destination: string;
}

export interface ScheduleShipment {
  shipmentId: number;
  reference: string;
  client: string;
  container: string;
  kg: number;
  arrived: boolean;
  journey: ShipmentTrackingSummary | null;
}

/**
 * A ship sailing (or, for air, one AWB) with the shipments on board. Dates
 * are calendar days from today: negative in the past.
 */
export interface ScheduleTrip {
  key: string;
  mode: ArrivalMode;
  title: string;
  carrier: string;
  voyage: string;
  pod: string;
  portName: string;
  flag: string;
  plannedEtd: number | null;
  plannedEta: number | null;
  etd: number;
  eta: number;
  etaSource: Arrival['etaSource'];
  arrived: boolean;
  /** Days later than planned at destination; 0 or less is on time. */
  delay: number | null;
  /** Two or more legs when the cargo is transshipped. */
  legs: ScheduleLeg[];
  shipments: ScheduleShipment[];
  kg: number;
}

const text = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const ETA_RANK: Record<NonNullable<Arrival['etaSource']>, number> = {
  actual: 3,
  tracking: 2,
  planned: 1,
};

/** The legs of the first transshipped journey among these shipments. */
function legsFor(
  shipmentIds: Set<number>,
  shipTrips: ShipmentShipTrip[],
  offset: (value: string | null | undefined) => number | null
): ScheduleLeg[] {
  const byJourney = new Map<string, ShipmentShipTrip[]>();
  for (const trip of shipTrips) {
    if (!shipmentIds.has(Number(trip.shipment_id))) continue;
    const list = byJourney.get(trip.tracking_journey_id) ?? [];
    list.push(trip);
    byJourney.set(trip.tracking_journey_id, list);
  }
  for (const list of byJourney.values()) {
    const legs = [...list]
      .sort((a, b) => a.leg_sequence - b.leg_sequence)
      .map((trip) => ({
        vessel: text(trip.ship_name) || 'Sin nave',
        start: offset(
          trip.atd ?? trip.etd ?? trip.live_etd ?? trip.planned_etd
        ),
        end: offset(trip.ata ?? trip.eta ?? trip.live_eta ?? trip.planned_eta),
        destination: text(trip.destination_name),
      }));
    if (
      legs.length > 1 &&
      legs.every(
        (leg): leg is ScheduleLeg =>
          leg.start !== null && leg.end !== null && leg.end >= leg.start
      )
    )
      return legs;
  }
  return [];
}

/**
 * Groups the Gestor de Arribos rows by ship sailing (ocean) or AWB (air).
 * The original plan comes from the uploaded ETD/ETA; the real dates from
 * tracking, falling back to the plan.
 */
export function buildScheduleTrips(
  arrivals: Arrival[],
  shipTrips: ShipmentShipTrip[],
  today: Date
): { trips: ScheduleTrip[]; withoutDates: number } {
  const offset = (value: string | null | undefined) =>
    value ? getShipmentCalendarDayOffset(value, today) : null;
  const groups = new Map<string, Arrival[]>();
  for (const arrival of arrivals) {
    const voyage = arrival.voyage === '—' ? '' : arrival.voyage;
    const key =
      arrival.mode === 'air'
        ? JSON.stringify(['air', arrival.containerKey, arrival.pod])
        : JSON.stringify([
            'ocean',
            arrival.vessel.toUpperCase(),
            voyage,
            arrival.pod,
            // Without a voyage, the departure day separates sailings.
            voyage ? '' : arrival.etd,
          ]);
    groups.set(key, [...(groups.get(key) ?? []), arrival]);
  }

  const trips: ScheduleTrip[] = [];
  let withoutDates = 0;
  for (const [key, group] of groups) {
    const first = group[0];
    const etds = group.flatMap((a) => (a.etd ? [offset(a.etd)!] : []));
    const etas = group.flatMap((a) => (a.eta ? [offset(a.eta)!] : []));
    const plannedEtds = group.flatMap((a) =>
      a.plannedEtd ? [offset(a.plannedEtd)!] : []
    );
    const plannedEtas = group.flatMap((a) =>
      a.plannedEta ? [offset(a.plannedEta)!] : []
    );
    const shipmentIds = new Set(group.map((a) => a.shipmentId));
    if (!etds.length || !etas.length) {
      withoutDates += shipmentIds.size;
      continue;
    }
    const etd = Math.min(...etds);
    const eta = Math.max(etd, ...etas);
    const etaSource = group.reduce<Arrival['etaSource']>(
      (best, a) =>
        a.etaSource && (!best || ETA_RANK[a.etaSource] > ETA_RANK[best])
          ? a.etaSource
          : best,
      null
    );
    const plannedEtd = plannedEtds.length ? Math.min(...plannedEtds) : null;
    const plannedEta = plannedEtas.length ? Math.max(...plannedEtas) : null;

    const shipments = new Map<string, ScheduleShipment>();
    for (const arrival of group) {
      const id = JSON.stringify([arrival.shipmentId, arrival.container]);
      const current = shipments.get(id);
      if (current) {
        current.kg += arrival.kg;
        if (!current.client.includes(arrival.client))
          current.client += `, ${arrival.client}`;
      } else {
        shipments.set(id, {
          shipmentId: arrival.shipmentId,
          reference: arrival.reference,
          client: arrival.client,
          container: arrival.container,
          kg: arrival.kg,
          arrived: arrival.arrived,
          journey: arrival.journey,
        });
      }
    }

    const legs =
      first.mode === 'ocean' ? legsFor(shipmentIds, shipTrips, offset) : [];
    const carrier =
      first.mode === 'air'
        ? first.vessel
        : text(
            group.find((a) => a.journey?.carrier_name)?.journey?.carrier_name
          );
    trips.push({
      key,
      mode: first.mode,
      title:
        first.mode === 'air'
          ? first.container
          : legs.length
            ? legs.map((leg) => leg.vessel).join(' → ')
            : first.vessel,
      carrier:
        carrier || (first.mode === 'air' ? 'Sin aerolínea' : 'Sin naviera'),
      voyage: first.voyage === '—' ? '' : first.voyage,
      pod: first.pod,
      portName: first.portName,
      flag: first.flag,
      plannedEtd,
      plannedEta,
      etd: legs.length ? legs[0].start : etd,
      eta: legs.length ? Math.max(eta, legs[legs.length - 1].end) : eta,
      etaSource,
      arrived: group.every((a) => a.arrived),
      delay:
        plannedEta !== null && etaSource && etaSource !== 'planned'
          ? eta - plannedEta
          : null,
      legs,
      shipments: [...shipments.values()].sort((a, b) =>
        a.reference.localeCompare(b.reference)
      ),
      kg: group.reduce((total, a) => total + a.kg, 0),
    });
  }
  return { trips, withoutDates };
}

export function sortScheduleTrips(trips: ScheduleTrip[], sort: ScheduleSort) {
  return [...trips].sort((a, b) => {
    if (sort === 'ETA real') return a.eta - b.eta || a.etd - b.etd;
    if (sort === 'Atraso')
      return (b.delay ?? -Infinity) - (a.delay ?? -Infinity) || a.eta - b.eta;
    return a.etd - b.etd || a.eta - b.eta;
  });
}

/** Trips whose span touches the window [from, to] (days from today). */
export const tripsInWindow = (
  trips: ScheduleTrip[],
  from: number,
  to: number
) => trips.filter((trip) => trip.eta >= from && trip.etd <= to);
